import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import pages from "@/config/pages";
import type { Group } from "@/types/group/group";
import { Link as LinkIcon, MoveRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";

interface GroupsTableProps {
	groups: Group[];
}

export const GroupsTable = ({ groups }: GroupsTableProps) => {
	const { t } = useTranslation();
	return (
		<Table className="table-fixed w-full" aria-label="Ansattgrupper">
			<TableHeader>
				<TableRow className="bg-moss-green/5 border-b">
					<TableHead className="py-2 px-4 text-xs w-1/4">
						{t("admin.table.group-name")}
					</TableHead>
					<TableHead className="py-2 px-4 text-xs w-1/4">
						{t("admin.table.products")}
					</TableHead>
					<TableHead className="py-2 px-4 text-xs w-1/4">
						{t("admin.table.portal")}
					</TableHead>
					<TableHead className="py-2 px-4 text-xs w-1/4">
						{t("admin.table.calculations")}
					</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{groups.length === 0 && (
					<TableRow>
						<TableCell
							colSpan={4}
							className="text-center text-muted-foreground"
						>
							{t("admin.table.message.no-groups")}
						</TableCell>
					</TableRow>
				)}
				{groups.map((group: Group) => (
					<GroupsTableRow group={group} key={group.id} />
				))}
			</TableBody>
		</Table>
	);
};

interface GroupsTableRowProps {
	group: Group;
}

const GroupsTableRow = ({ group }: GroupsTableRowProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { mainAccId } = useParams();

	return (
		<TableRow key={group.id}>
			<TableCell className="py-2 px-4 text-xs truncate">{group.name}</TableCell>
			<TableCell className="py-2 px-4 text-xs">
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="cursor-pointer hover:text-gold underline decoration-dotted">
							{/* {groupProductCounts.get(group.id) || 0} produkter */}
							{group.insurances.length ?? []} {t("admin.table.products")}
						</span>
					</TooltipTrigger>
					<TooltipContent side="right" className="max-w-[300px]">
						<p className="font-medium mb-1">
							{t("admin.table.message.products-in-group")} {group.name}:
						</p>
						<div className="text-xs">
							{group.insurances.length > 0 ? (
								<ul className="list-disc pl-4 space-y-0.5">
									{group.insurances.map((insurance, idx) => (
										<li key={idx}>
											{t("admin.insurances.type-to-name." + insurance.type)}
										</li>
									))}
								</ul>
							) : (
								<p className="text-muted-foreground">
									{t("admin.table.no-products")}
								</p>
							)}
						</div>
					</TooltipContent>
				</Tooltip>
			</TableCell>
			<TableCell className="py-2 px-4">
				<Button
					size="sm"
					variant="secondary"
					className="h-7 px-2 text-xs"
					onClick={() => {
						navigator.clipboard
							.writeText(new URL(group.id, location.origin).toString())
							.then(() => toast.success("Group link copied"))
							.catch(() => toast.error("Could not copy group link"));
					}}
					aria-label={`${group.name}`}
				>
					<LinkIcon className="h-3 w-3 mr-1" />
					{t("admin.table.copy")}
				</Button>
			</TableCell>
			<TableCell className="py-2 px-4">
				<Button
					size="sm"
					variant="link"
					className="text-xs text-gold hover:text-gold-90 p-0 h-auto"
					onClick={() =>
						navigate(
							pages.AdminCoverageMatrix(mainAccId!, group.account_id, group.id)
						)
					}
					aria-label={`Åpne kalkulator for gruppe ${group.name}`}
				>
					{t("admin.table.coverage-overview")}
					<MoveRight className="h-3 w-3 ml-1" />
				</Button>
			</TableCell>
		</TableRow>
	);
};
